import { Stack, Card, Box, IconButton, Typography, Button, Tooltip } from "@mui/material";
import React, { useCallback, useContext, useState } from "react";
import Drawer from "../../ui/Drawer";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { getNegativeValueInAccountingForm } from "../../utility/format-display-output";
import PDFsContainerComponent from "../pdf-container-component/hoa-pool-access-form-pdf-container";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { checkVisibility } from "../../utility/data-configurator-helper";
import datastore from "../../store/hoa-datastore";

import { urlCheck } from "../../utility/url-check";

const AccessInformationComponent: React.FC<{ accessInformation: any }> = ({
	accessInformation,
}) => {
	const [openDrawer, toggleDrawer] = useState(false);

	const [selected, setSelected] = useState<number | null>(null);

	const handleViewAccessInformation = (index: number) => {
		toggleDrawer(true);
		setSelected(index);
	};

	const closeDrawer = () => {
		toggleDrawer(false);
		setSelected(null);
	};

	const isAmenityVisible = useCallback(
		(type: string) => {
			if (Object.keys(datastore.selectedConfiguration).length === 0) {
				return true;
			}
			const dataConfigAmenities =
				datastore.selectedConfiguration["Amenities"]["Access Information"][
					"Show the added access information"
				];
			return checkVisibility(dataConfigAmenities, type);
		},
		[datastore.selectedConfiguration]
	);
	return (
		<Stack gap={2}>
			<span className="text-3E3E3E-18px bold">Access Information</span>
			<Stack gap={2}>
				{accessInformation.map((info: any, index: number) => {
					return (
						<Card
							style={{
								borderColor: selected === index ? "#3576E5" : "#E8E8E8",
								background: selected === index ? "#EFF4FC" : "#FFFFFF",
								cursor: "pointer",
							}}
							sx={{ "&:hover": { borderColor: "#3576E5 !important" } }}
							key={index}
							onClick={() => handleViewAccessInformation(index)}
							className="access-info-card"
							variant="outlined"
						>
							{isAmenityVisible("Name") && (
								<span className="text-3E3E3E-16px">{info.name}</span>
							)}

							{isAmenityVisible("Access Proceedure") && (
								<span
									style={{
										fontWeight: "400",
										borderBottom: "1px solid #E8E8E8",
										paddingBottom: 8,
									}}
									className="text-787878-12px"
								>
									{info.accessProcedure}
								</span>
							)}
							{[
								{ name: "First Fee", value: "firstFee" },
								{ name: "Access Fee", value: "accessFee" },
								{ name: "Replacement Fee", value: "replacementFee" },
							].map((feeDetails: any, index: number) => {
								return (
									isAmenityVisible(feeDetails.name) && (
										<Stack flexDirection={"row"} key={index}>
											<span
												style={{ width: "200px" }}
												className="text-787878-16px"
											>
												{feeDetails.name}
											</span>
											<span className="text-303030-16px">
												{`${getNegativeValueInAccountingForm(
													info[feeDetails.value]
												)}`}
											</span>
										</Stack>
									)
								);
							})}
						</Card>
					);
				})}
			</Stack>
			<Drawer open={openDrawer} closeDrawer={closeDrawer}>
				<AmenityAccessInfoDetails
					closeDrawer={closeDrawer}
					accessInformation={accessInformation[selected]}
					isAmenityVisible={isAmenityVisible}
				/>
			</Drawer>
		</Stack>
	);
};

export default AccessInformationComponent;

const AccessInformationHeader = ({ closeDrawer }: { closeDrawer: any }) => (
	<Stack
		direction={"row"}
		position={"sticky"}
		zIndex={9999}
		top={0}
		sx={{ background: "#FFFFFF" }}
		justifyContent={"space-between"}
		alignItems={"center"}
		pl={"24px"}
		pr={"24px"}
		height={72}
		borderBottom={"1px solid #E8E8E8"}
	>
		<Box display={"flex"} alignItems={"center"}>
			<Typography fontSize={20} fontWeight={500} color={"#2F2F2F"}>
				Access Information
			</Typography>
		</Box>
		<Box>
			<IconButton onClick={closeDrawer}>
				<CloseOutlinedIcon sx={{ color: "#2F2F2F" }} />
			</IconButton>
		</Box>
	</Stack>
);

const AmenityAccessInfoDetails = ({
	closeDrawer,
	accessInformation,
	isAmenityVisible,
}: {
	closeDrawer: any;
	accessInformation: any;
	isAmenityVisible: any;
}) => {
	const onFileDownloadClick = (url: string) => {
		if (url !== "N/A") {
			window.open(urlCheck(url), "_blank");
		}
	};

	return (
		<Box sx={{ width: "623px", wordBreak: "break-all" }} role="presentation">
			<AccessInformationHeader closeDrawer={closeDrawer} />
			<Stack p={"24px"} gap={"24px"}>
				<Stack direction={"row"} pb={"24px"} borderBottom={"1px solid #E8E8E8"}>
					{isAmenityVisible("Name") && (
						<Stack direction={"column"} gap={"8px"} width={296}>
							<Typography fontSize={14} fontWeight={500} color={"#787878"}>
								Name
							</Typography>
							<Typography fontSize={16} color={"#303030"}>
								{accessInformation?.name || "N/A"}
							</Typography>
						</Stack>
					)}
					{isAmenityVisible("Access Fee") && (
						<Stack direction={"column"} gap={"8px"}>
							<Typography fontSize={14} fontWeight={500} color={"#787878"}>
								Access Fee
							</Typography>
							<Typography fontSize={16} color={"#303030"}>
								{getNegativeValueInAccountingForm(accessInformation?.accessFee) ||
									"N/A"}
							</Typography>
						</Stack>
					)}
				</Stack>
				{isAmenityVisible("Access Proceedure") && (
					<Stack pb={"24px"} borderBottom={"1px solid #E8E8E8"}>
						<Typography fontSize={14} fontWeight={500} color={"#787878"}>
							Access Procedure
						</Typography>
						<Typography width={429} fontSize={14} fontWeight={500} color={"#5B5B5B"}>
							{accessInformation?.accessProcedure || "N/A"}
						</Typography>
					</Stack>
				)}
				<Stack pb={"24px"} borderBottom={"1px solid #E8E8E8"} direction={"row"}>
					{isAmenityVisible("First Fee") && (
						<Stack direction={"column"} gap={"8px"} width={128}>
							<Typography fontSize={14} fontWeight={500} color={"#787878"}>
								First Fee
							</Typography>
							<Typography fontSize={16} color={"#303030"}>
								{getNegativeValueInAccountingForm(accessInformation?.firstFee) ||
									"N/A"}
							</Typography>
						</Stack>
					)}
					{isAmenityVisible("Replacement Fee") && (
						<Stack direction={"column"} gap={"8px"} width={170}>
							<Typography fontSize={14} fontWeight={500} color={"#787878"}>
								Replacement Fee
							</Typography>
							<Typography fontSize={16} color={"#303030"}>
								{getNegativeValueInAccountingForm(
									accessInformation?.replacementFee
								) || "N/A"}
							</Typography>
						</Stack>
					)}
					{isAmenityVisible("Payment Form Link") && (
						<Stack direction={"column"} gap={"8px"}>
							<Typography fontSize={14} fontWeight={500} color={"#787878"}>
								Payment Form
							</Typography>
							{accessInformation?.paymentFormLink ? (
								<Stack
									gap={1}
									direction={"row"}
									alignItems={"flex-start"}
									justifyContent={"center"}
								>
									<span className="text-3576E5-14px">Open Link</span>
									<Tooltip title="Open Link">
										<IconButton
											style={{ padding: "2px" }}
											onClick={() =>
												onFileDownloadClick(
													accessInformation?.paymentFormLink
												)
											}
										>
											<OpenInNewIcon
												style={{
													padding: "0px",
													width: "15px",
													height: "15px",
													color: "#3576E5",
												}}
											/>
										</IconButton>
									</Tooltip>
								</Stack>
							) : (
								<Box>
									<Typography fontSize={14} color={"#303030"}>
										N/A
									</Typography>
								</Box>
							)}
						</Stack>
					)}
				</Stack>
				{isAmenityVisible("Get Access Information") && (
					<Box pb={"24px"}>
						<Typography fontSize={18} color={"#3E3E3E"} fontWeight={500}>
							Get Access Information
						</Typography>
						<Stack direction={"row"} gap={"16px"} mt={1}>
							{accessInformation?.getAccessInformation?.length ? (
								accessInformation?.getAccessInformation.map(
									(info: any, index: number) => (
										<Button
											key={index}
											style={{
												borderColor: "#C5C5C5",
												color: "#5B5B5B",
												background: "#F2F2F2",
												width: "124px",
												height: "36px",
											}}
											variant="outlined"
										>
											{info}
										</Button>
									)
								)
							) : (
								<Box mt={2}>
									<Typography fontSize={14} color={"#303030"}>
										N/A
									</Typography>
								</Box>
							)}
						</Stack>
					</Box>
				)}

				{isAmenityVisible("Access Form") && (
					<Box>
						<Typography fontSize={18} color={"#3E3E3E"} fontWeight={500}>
							Access Form
						</Typography>
						<Typography fontSize={14} color={"#787878"} pt={"24px"} fontWeight={500}>
							Documents
						</Typography>
						<Stack direction={"column"}>
							{accessInformation?.accessForm?.length ? (
								accessInformation.accessForm.map((file: any, index: number) => (
									<PDFsContainerComponent
										view={"view"}
										key={index}
										file={file}
										path="hoa"
										folder="accessForm"
									/>
								))
							) : (
								<Box mt={2}>
									<Typography fontSize={14} color={"#303030"}>
										N/A
									</Typography>
								</Box>
							)}
						</Stack>
					</Box>
				)}

				{isAmenityVisible("Access Form Link") && (
					<Stack direction={"column"} gap={"8px"}>
						<Typography fontSize={14} fontWeight={500} color={"#787878"}>
							Access Form Link
						</Typography>
						{accessInformation?.accessFormLink ? (
							<Stack gap={1} direction={"row"} alignItems={"center"}>
								<span className="text-3576E5-14px">Open Link</span>
								<Tooltip title="Open Link">
									<IconButton
										style={{ padding: "2px" }}
										onClick={() =>
											onFileDownloadClick(accessInformation.accessFormLink)
										}
									>
										<OpenInNewIcon
											style={{
												width: "15px",
												height: "15px",
												color: "#3576E5",
											}}
										/>
									</IconButton>
								</Tooltip>
							</Stack>
						) : (
							<Box>
								<Typography fontSize={14} color={"#303030"}>
									N/A
								</Typography>
							</Box>
						)}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};
